import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import {
  ITimeInterval,
  TIME_INTERVAL_TRANSFORMER_SERVICE,
  TimeIntervalTransformerService,
} from '@luggagehero/shared/interfaces';

import { BaseComponent } from '../../../core';
import { TimeInterval } from '../../../services';
import { OpeningHours } from '../../../services/shops/opening-hours';

@Component({ template: '' })
export abstract class ManageOpeningHoursBaseComponent extends BaseComponent implements AfterViewInit {
  private timeIntervalTransformerService = inject<TimeIntervalTransformerService>(TIME_INTERVAL_TRANSFORMER_SERVICE);
  @Input() openingHours: OpeningHours;
  @Output() openingHoursChange: EventEmitter<OpeningHours>;
  @Output() pristineChange: EventEmitter<boolean>;

  private _pristine = true;
  private isInitialized = false;

  constructor(private cd: ChangeDetectorRef) {
    super();

    this.openingHoursChange = new EventEmitter<OpeningHours>();
    this.pristineChange = new EventEmitter<boolean>();
  }

  get pristine(): boolean {
    return this._pristine;
  }
  set pristine(value: boolean) {
    this._pristine = value;
    this.cd.markForCheck();

    this.pristineChange.emit(value);
  }

  get valid(): boolean {
    return this.errors ? false : true;
  }

  get errors(): ValidationErrors {
    if (OpeningHours.countOpenHours(this.openingHours) < this.minHours) {
      // The number of open hours is less than the minimum required
      return { min: true };
    }

    if (!OpeningHours.isValid(this.openingHours)) {
      return { invalid: true };
    }

    return null;
  }

  get minHours(): number {
    return AppConfig.DEFAULT_MIN_STORAGE_LOCATION_OPEN_HOURS;
  }

  ngAfterViewInit() {
    this.isInitialized = true;
  }

  onChange(_e) {
    this.openingHoursChange.emit(this.openingHours);

    if (this.isInitialized) {
      this.pristine = false;
    }
  }

  setAlwaysOpen() {
    this.openingHours.monday = [{ from: 0, to: 1440 }];
    this.openingHours.tuesday = [{ from: 0, to: 1440 }];
    this.openingHours.wednesday = [{ from: 0, to: 1440 }];
    this.openingHours.thursday = [{ from: 0, to: 1440 }];
    this.openingHours.friday = [{ from: 0, to: 1440 }];
    this.openingHours.saturday = [{ from: 0, to: 1440 }];
    this.openingHours.sunday = [{ from: 0, to: 1440 }];
    this.cd.markForCheck();
  }

  copyHours(from: string, to?: string) {
    const destinations = to ? [to] : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    for (const destination of destinations) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.openingHours[destination] = TimeInterval.copy(this.openingHours[from]);
    }
    this.cd.markForCheck();
  }

  hasValidHours(day: string): boolean {
    const openingHours = this.openingHours[day] as ITimeInterval[];

    if (openingHours === null || openingHours.length === 0) {
      return true;
    }
    for (const timeInterval of openingHours) {
      if (!TimeInterval.isValid(timeInterval)) {
        return false;
      }
    }
    return true;
  }
}
